/**
 * Normalize FastAPI / Pydantic ``detail`` into a single toast-safe string.
 * Never return objects/arrays — React will crash if those are rendered as children.
 */
export function formatApiErrorDetail(detail) {
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    const parts = detail.slice(0, 3).map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const loc = Array.isArray(entry.loc)
        ? entry.loc.filter((x) => x !== 'body').join('.')
        : '';
      const msg = typeof entry.msg === 'string' ? entry.msg : '';
      if (loc && msg) return `${loc}: ${msg}`;
      return msg || loc || null;
    }).filter(Boolean);
    if (parts.length) {
      const more = detail.length > parts.length ? ` (+${detail.length - parts.length} more)` : '';
      return `${parts.join(' · ')}${more}`;
    }
  }
  if (detail && typeof detail === 'object') {
    const msg = typeof detail.msg === 'string' ? detail.msg : '';
    if (msg) return msg;
  }
  return '';
}

export function apiErrorMessage(error, fallback = 'Request failed') {
  const msg = formatApiErrorDetail(error?.response?.data?.detail);
  return msg || fallback;
}

/** Always pass a plain string to toast — never objects/arrays (React #31). */
export function safeToastError(toastFn, errorOrMessage, fallback = 'Request failed') {
  let text = fallback;
  if (typeof errorOrMessage === 'string' && errorOrMessage.trim()) {
    text = errorOrMessage.trim();
  } else if (errorOrMessage && typeof errorOrMessage === 'object') {
    text = apiErrorMessage(errorOrMessage, fallback);
  }
  toastFn(String(text || fallback));
}

/** Empty / incomplete → null so EmailStr validation is not hit mid-edit. */
export function optionalEmailOrNull(value) {
  const s = String(value || '').trim();
  if (!s) return null;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)) return null;
  return s;
}

/**
 * Soft-parse a comma/semicolon list while typing: keep only complete addresses.
 * Returns canonical ``a@x, b@y`` or null.
 */
export function optionalEmailListOrNull(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parts = raw.split(/[,;\n]+/);
  const emails = [];
  const seen = new Set();
  for (const part of parts) {
    const addr = optionalEmailOrNull(part);
    if (!addr || seen.has(addr)) continue;
    seen.add(addr);
    emails.push(addr);
  }
  return emails.length ? emails.join(', ') : null;
}
